import React from 'react'
import { Link } from 'react-router-dom'
import { Users, UserPlus, ArrowRight } from 'lucide-react'
import { PublicFooter } from '../components/layout/PublicFooter'
import { PublicNavigation } from '../components/layout/PublicNavigation'
import { Button } from '../components/ui/Button'

export const PartnersPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 relative overflow-hidden transition-colors duration-300">
      <PublicNavigation />

      {/* Hero Section */}
      <section className="py-20 lg:py-32 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#69adba]/10 dark:bg-[#69adba]/20 rounded-full mb-6">
              <UserPlus className="h-8 w-8 text-[#69adba]" />
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-100 sm:text-6xl mb-6">
              Partner With Us
            </h1>
            <p className="mt-6 text-lg leading-8 text-slate-600 dark:text-slate-400">
              Join our partner network and help your clients complete successful 1031 exchanges.
            </p>
          </div>
        </div>
      </section>

      {/* Partner Types */}
      <section className="py-20 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Real Estate Brokers */}
            <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 text-center">
              <div className="h-12 w-12 rounded-xl bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center mb-4 mx-auto">
                <Users className="h-6 w-6 text-[#69adba]" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                Real Estate Brokers
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-6">
                Offer your clients a seamless 1031 exchange experience with our integrated platform.
              </p>
              <Link to="/contact">
                <Button className="bg-[#69adba] hover:bg-[#5a9aa6] text-white">
                  Become a Partner
                </Button>
              </Link>
            </div>

            {/* Financial Advisors */}
            <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 text-center">
              <div className="h-12 w-12 rounded-xl bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center mb-4 mx-auto">
                <UserPlus className="h-6 w-6 text-[#69adba]" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                Financial Advisors
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-6">
                Provide comprehensive tax planning services with our 1031 exchange solutions.
              </p>
              <Link to="/contact">
                <Button className="bg-[#69adba] hover:bg-[#5a9aa6] text-white">
                  Become a Partner
                </Button>
              </Link>
            </div>

            {/* Technology Partners */}
            <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 text-center">
              <div className="h-12 w-12 rounded-xl bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center mb-4 mx-auto">
                <Users className="h-6 w-6 text-[#69adba]" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                Technology Partners
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-6">
                Integrate with our API to build powerful 1031 exchange solutions for your platform.
              </p>
              <Link to="/contact">
                <Button className="bg-[#69adba] hover:bg-[#5a9aa6] text-white">
                  Become a Partner
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-slate-50 dark:bg-slate-800/50 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100 text-center mb-12">
              Partner Benefits
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white dark:bg-slate-800 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                  Revenue Sharing
                </h3>
                <p className="text-slate-600 dark:text-slate-400">
                  Earn competitive commissions for referrals and successful exchanges.
                </p>
              </div>
              <div className="bg-white dark:bg-slate-800 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                  Dedicated Support
                </h3>
                <p className="text-slate-600 dark:text-slate-400">
                  Access to a dedicated partner success manager and priority support.
                </p>
              </div>
              <div className="bg-white dark:bg-slate-800 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                  Marketing Resources
                </h3>
                <p className="text-slate-600 dark:text-slate-400">
                  Co-marketing opportunities and materials to help promote your services.
                </p>
              </div>
              <div className="bg-white dark:bg-slate-800 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                  Training & Certification
                </h3>
                <p className="text-slate-600 dark:text-slate-400">
                  Comprehensive training programs to help you become a 1031 exchange expert.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#69adba] dark:bg-[#5a9aa6]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl mb-4">
            Ready to Partner With Us?
          </h2>
          <p className="mt-4 text-lg text-white/90 mb-8">
            Let's discuss how we can work together to serve your clients better.
          </p>
          <Link to="/contact">
            <Button className="bg-white text-[#69adba] hover:bg-slate-50">
              Contact Partnership Team
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
  )
}

